import { getErrorMessage, getErrorStatus } from '@/api/errors'
import {
  DeleteScheduleDialog,
  ScheduleFormDialog,
  ScheduleHistoryTable,
  ScheduleLatestFailure,
  ScheduleStateBadge,
  TargetInactiveBadge,
  describeRecurrence,
  formatInBrowserTimeZone,
  formatInTimeZone,
} from '@/components/projects/schedules'
import { Button, Card } from '@/components/ui'
import { useAuth, usePauseWorkflowSchedule, useProject, useResumeWorkflowSchedule, useWorkflowSchedule } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { extractValidationMessage } from '@/lib/validationErrors'
import { WORKFLOW_SCHEDULE_PAUSED_REASON_LABELS, type WorkflowSchedule, type WorkflowSchedulePausedReason } from '@/types'
import { AlertCircle, AlertTriangle, ArrowLeft, CirclePause, CirclePlay, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const OWNER_ONLY_HINT_ID = 'schedule-owner-only-hint'
const LATEST_FAILURE_PAUSED_REASONS: WorkflowSchedulePausedReason[] = ['AutoPausedAfterFailures', 'TargetDeleted']

function pausedByText(schedule: WorkflowSchedule): string {
  if (!schedule.pausedByUserId) return 'the system'
  return schedule.pausedByDisplayName ?? 'a user'
}

function pausedHeading(schedule: WorkflowSchedule): string {
  if (schedule.pausedReason === 'User') return `Paused by ${pausedByText(schedule)}`
  return schedule.pausedReason ? WORKFLOW_SCHEDULE_PAUSED_REASON_LABELS[schedule.pausedReason] : 'This schedule is paused'
}

function OverviewItem({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0" data-testid={testId}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-foreground">{children}</dd>
    </div>
  )
}

export function WorkflowScheduleDetailPage() {
  const { projectId, scheduleId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isAdmin, user, isLoading: authLoading } = useAuth(projectId)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: schedule, isLoading, isError, error, refetch } = useWorkflowSchedule(projectId!, scheduleId)
  const { data: project } = useProject(projectId)
  const pauseSchedule = usePauseWorkflowSchedule(projectId!)
  const resumeSchedule = useResumeWorkflowSchedule(projectId!)

  if (authLoading) return null
  if (!hasPermission(Permissions.Project.Schedule.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  const backToList = () => navigate(`/projects/${projectId}/workflows/schedules`)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite" data-testid="schedule-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading schedule" />
      </div>
    )
  }

  const errorStatus = isError ? getErrorStatus(error) : undefined

  if (errorStatus === 403) {
    return (
      <div className="p-6" role="alert" data-testid="schedule-detail-forbidden">
        <p className="text-muted-foreground">You do not have permission to view this schedule.</p>
        <Button variant="ghost" className="mt-2" onClick={backToList}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to schedules
        </Button>
      </div>
    )
  }

  if (isError && errorStatus !== 404) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16" role="alert" data-testid="schedule-detail-error">
          <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
          <h3 className="mt-4 text-lg font-semibold">Failed to load schedule</h3>
          <p className="mt-1 text-sm text-muted-foreground">Something went wrong while loading this schedule.</p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  if (!schedule) {
    return (
      <div className="p-6" data-testid="schedule-detail-not-found">
        <p className="text-muted-foreground">Schedule not found. It may have been deleted.</p>
        <Button variant="ghost" className="mt-2" onClick={backToList}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to schedules
        </Button>
      </div>
    )
  }

  const isOwner = !!user && user.id === schedule.ownerUserId
  const canEditPermission = hasPermission(Permissions.Project.Schedule.Edit, projectId)
  const canDeletePermission = hasPermission(Permissions.Project.Schedule.Delete, projectId)
  const ownerOrAdmin = isOwner || isAdmin
  const showOwnerOnlyHint = (canEditPermission || canDeletePermission) && !ownerOrAdmin
  const isPaused = schedule.state === 'Paused'
  const isTargetInactive = !!schedule.targetName && !schedule.targetIsActive
  const isToggling = pauseSchedule.isPending || resumeSchedule.isPending

  const handleTogglePause = async () => {
    try {
      if (isPaused) {
        await resumeSchedule.mutateAsync(schedule.id)
        toast.success(`Schedule "${schedule.name}" resumed`)
      } else {
        await pauseSchedule.mutateAsync(schedule.id)
        toast.success(`Schedule "${schedule.name}" paused`)
      }
    } catch (err) {
      toast.error(extractValidationMessage(err) ?? getErrorMessage(err) ?? `Failed to ${isPaused ? 'resume' : 'pause'} the schedule.`)
    }
  }

  return (
    <div className="space-y-6" data-testid="schedule-detail-page">
      <div>
        {project && <p className="text-sm font-medium text-card-foreground">Project: {project.name}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={backToList} aria-label="Back to schedules">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold break-words min-w-0">{schedule.name}</h1>
          <ScheduleStateBadge state={schedule.state} />
          <div className="ml-auto flex items-center gap-2">
            {canEditPermission && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                disabled={!ownerOrAdmin}
                aria-describedby={showOwnerOnlyHint ? OWNER_ONLY_HINT_ID : undefined}
              >
                <Pencil className="h-4 w-4 mr-1" aria-hidden="true" />
                Edit
              </Button>
            )}
            {canEditPermission && (
              <Button variant="outline" onClick={handleTogglePause} disabled={isToggling}>
                {isPaused
                  ? <><CirclePlay className="h-4 w-4 mr-1" aria-hidden="true" />Resume</>
                  : <><CirclePause className="h-4 w-4 mr-1" aria-hidden="true" />Pause</>}
              </Button>
            )}
            {canDeletePermission && (
              <Button
                variant="outline"
                className="text-danger-foreground"
                onClick={() => setDeleteOpen(true)}
                disabled={!ownerOrAdmin}
                aria-describedby={showOwnerOnlyHint ? OWNER_ONLY_HINT_ID : undefined}
              >
                <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
                Delete
              </Button>
            )}
          </div>
        </div>
        {showOwnerOnlyHint && (
          <p id={OWNER_ONLY_HINT_ID} className="mt-2 text-right text-xs text-muted-foreground" data-testid="schedule-owner-only-hint">
            Only the schedule owner or an administrator can edit or delete this schedule.
          </p>
        )}
        {schedule.description &&<p className="mt-2 text-sm text-muted-foreground break-words">{schedule.description}</p>}
      </div>

      {isPaused && (
        <div className="rounded-lg border border-warning-foreground bg-warning p-4" role="status" data-testid="schedule-paused-banner">
          <div className="flex items-start gap-2 text-warning-foreground">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-semibold">{pausedHeading(schedule)}</p>
              <p className="text-sm">
                Paused {schedule.pausedAtUtc ? `on ${formatInTimeZone(schedule.pausedAtUtc, schedule.timeZoneId)} ` : ''}by {pausedByText(schedule)}.
                It does not fire while paused, and nothing is reported missed. When resumed, only future occurrences fire.
              </p>
              {schedule.pausedReason && LATEST_FAILURE_PAUSED_REASONS.includes(schedule.pausedReason) && (
                <ScheduleLatestFailure projectId={projectId!} scheduleId={schedule.id} timeZoneId={schedule.timeZoneId} />
              )}
            </div>
          </div>
        </div>
      )}

      {isTargetInactive && (
        <div className="rounded-lg border border-warning-foreground bg-warning p-4" role="status" data-testid="schedule-target-inactive-banner">
          <div className="flex items-start gap-2 text-warning-foreground">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-semibold">The workflow {schedule.targetName} is deactivated</p>
              <p className="text-sm">
                This schedule can't start runs while its workflow is deactivated: each due time is recorded as failed, and after
                3 failures in a row the schedule pauses itself. Reactivate the workflow to let the schedule run again.
              </p>
            </div>
          </div>
        </div>
      )}

      <Card className="p-6" data-testid="schedule-overview">
        <h2 className="text-xl font-bold mb-4">Overview</h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-3">
          <OverviewItem label="Workflow" testId="schedule-overview-workflow">
            {schedule.targetName ? (
              <div className="flex flex-col items-start gap-1">
                <Link
                  to={`/projects/${projectId}/workflows/definitions/${schedule.targetDefinitionId}`}
                  className="font-medium hover:underline"
                >
                  {schedule.targetName}
                </Link>
                {isTargetInactive && <TargetInactiveBadge />}
              </div>
            ) : (
              <span className="italic text-muted-foreground">Deleted workflow</span>
            )}
          </OverviewItem>
          <OverviewItem label="Recurrence" testId="schedule-overview-recurrence">
            <span className={schedule.recurrenceKind === 'Cron' ? 'font-mono' : undefined}>
              {describeRecurrence(schedule.recurrenceKind, schedule.cronExpression, schedule.intervalMinutes)}
            </span>
          </OverviewItem>
          <OverviewItem label="Time zone" testId="schedule-overview-timezone">{schedule.timeZoneId}</OverviewItem>
          <OverviewItem label="Runs as" testId="schedule-overview-owner">
            {schedule.ownerDisplayName ?? 'Unknown user'} · owner
          </OverviewItem>
          <OverviewItem label="Next due" testId="schedule-overview-next-due">
            {isPaused || !schedule.nextOccurrenceUtc ? (
              <span className="text-muted-foreground">{isPaused ? 'Not while paused' : 'No upcoming occurrence'}</span>
            ) : (
              <div className="flex flex-col">
                <span>{formatInTimeZone(schedule.nextOccurrenceUtc, schedule.timeZoneId)}</span>
                <span className="text-xs text-muted-foreground">Your time: {formatInBrowserTimeZone(schedule.nextOccurrenceUtc)}</span>
              </div>
            )}
          </OverviewItem>
          <OverviewItem label="Consecutive failures" testId="schedule-overview-failures">
            {schedule.consecutiveFailureCount}
          </OverviewItem>
          <OverviewItem label="Created">{formatInBrowserTimeZone(schedule.creationTime)}</OverviewItem>
          <OverviewItem label="Last modified">{formatInBrowserTimeZone(schedule.lastModificationTime)}</OverviewItem>
          <OverviewItem label="Skipped / failed occurrences">
            {schedule.skippedCount} skipped · {schedule.failedCount} failed
          </OverviewItem>
        </dl>
      </Card>

      <ScheduleHistoryTable projectId={projectId!} scheduleId={schedule.id} timeZoneId={schedule.timeZoneId} />

      {canEditPermission && ownerOrAdmin && (
        <ScheduleFormDialog projectId={projectId!} open={editOpen} onOpenChange={setEditOpen} schedule={schedule} />
      )}
      <DeleteScheduleDialog
        projectId={projectId!}
        schedule={{ id: schedule.id, name: schedule.name }}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={backToList}
      />
    </div>
  )
}
